import React, { useEffect, useCallback, useState, useRef } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Zoom,
  FormControlLabel,
  Switch,
} from '@material-ui/core'
import styled from 'styled-components'
import { Button } from 'sputnik-ui'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  fetchDeviceServiceCatalog,
  UpdateDeviceServices,
} from 'features/Devices/devicesSlice'
import { isBeward as isBewardModel } from 'utils/thirdPartyIntercomKinds'

const StyledContent = styled(DialogContent)`
  && {
    padding: 1rem 70px;
    padding-top: 0px;
    overflow: auto;
    @media screen and (max-width: ${(p) => p.theme.sizes.phone}) {
      height: 70vh;
      padding: 0 2rem;
    }
  }
`

const StyledSection = styled.div`
  margin-bottom: 2rem;
`

const SectionTitle = styled(Typography)`
  && {
    font-weight: 500;
    margin-bottom: 1rem;
  }
`

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const OptionItem = styled(FormControlLabel)`
  && {
    margin-left: 0;
    margin-right: 0;
    align-items: flex-start;
    display: flex;
    .MuiFormControlLabel-label {
      margin-top: 5px;
    }
  }
`

const TotalSection = styled.div`
  text-align: center;
  font-weight: 500;
`

const StyledAction = styled(DialogActions)`
  && {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 24px 20px 24px 20px;
    > * {
      margin: 0;
    }
  }
`

const StyledDialog = styled(Dialog)`
  && {
    .MuiDialog-paper {
      width: 600px;
      max-width: 90vw;
      height: auto;

      @media screen and (max-width: ${(p) => p.theme.sizes.phone}) {
        height: 90vh; /* Only apply fixed height on mobile */
        overflow-y: auto; /* Enable scrolling only on mobile */
      }
    }
  }
`

function Transition(props) {
  return <Zoom direction="up" {...props} />
}

function ModalChangeDepthArchive({
  selected,
  open,
  handleClose,
  isCamera,
  service_identifier,
  onSuccess,
  beward,
  allowChange = false,
  ...props
}) {
  const dispatch = useDispatch()
  const { t } = useTranslation([
    'common',
    'devices',
    'time',
    'camera_archive_settings',
  ])

  const hasFetchedCatalog = useRef(false)

  const [archiveDepth, setArchiveDepth] = useState('')
  const [videoQuality, setVideoQuality] = useState('bitrate_1024')
  const [selectedDevice, setselectedDevice] = useState(null)
  const [totalPrice, setTotalPrice] = useState(0)
  const [archiveArchiveOptions, setArchiveArchiveOptions] = useState({
    info: [],
    group_name: '',
    group_description: '',
    group: '',
  })
  const [servicesChanged, setServicesChanged] = useState(false)
  const [videoQualityOptions, setvideoQualityOptions] = useState({
    group_name: t('devices:camera_archive_settings.bitrate_title'),
    group: 'video',
    group_description: t('devices:camera_archive_settings.bitrate_desc'),
    info: [
      {
        id: 'bitrate_1024',
        label: '2MP, 20 fps, 1024 kbps',
        cost: 0,
      },
    ],
  })

  const [servicesToChange, setServicesToChange] = useState({})

  const devices = useSelector((state) => state.devicesNew.list)
  const isBeward =
    beward ||
    isBewardModel(devices?.find((device) => device.uuid === selected)?.model)

  const handleArchiveDepthChange = (value) => {
    setArchiveDepth((prev) => {
      const newDepth = value === prev ? null : value
      const archiveCost =
        archiveArchiveOptions.info?.find((s) => s.id === newDepth)?.cost || 0
      const videoCost =
        videoQualityOptions.info?.find((s) => s.id === videoQuality)?.cost || 0
      setTotalPrice(archiveCost + videoCost)

      setServicesToChange((prevServices) => {
        const newServices = servicesToChange
        newServices[value] = !prevServices[value]
        if (prev) {
          newServices[prev] = false
        }
        return newServices
      })

      return newDepth
    })
    setServicesChanged(true)
  }

  const handleVideoQualityChange = (id) => {
    setVideoQuality((prev) => {
      const archiveCost =
        archiveArchiveOptions.info?.find((s) => s.id === archiveDepth)?.cost ||
        0
      const videoCost =
        videoQualityOptions.info?.find((s) => s.id === id)?.cost || 0
      setTotalPrice(archiveCost + videoCost)
      return id
    })
    setServicesChanged(true)
  }

  useEffect(() => {
    hasFetchedCatalog.current = false
  }, [selected])

  useEffect(() => {
    if (open && selected && !hasFetchedCatalog.current) {
      const currentDevice = devices?.find((device) => device.uuid === selected)
      setselectedDevice(currentDevice)
      setArchiveDepth(null)
      let archiveDepthLocal
      if (currentDevice?.services) {
        archiveDepthLocal = currentDevice?.services.find((s) =>
          s.startsWith('archive_')
        )
        setArchiveDepth(archiveDepthLocal)
      }

      hasFetchedCatalog.current = true

      const fetchData = async () => {
        try {
          const result = await dispatch(
            fetchDeviceServiceCatalog({
              device_uuid: selected,
              device_type: isCamera ? 'camera' : 'intercom',
            })
          ).then(unwrapResult)

          const archiveOptions = {
            info: [],
            group_name: '',
            group_description: '',
            group: '',
          }

          const videoOptions = {
            info: [],
            group_name: '',
            group_description: '',
          }

          const services = {}
          if (archiveDepthLocal) {
            services[archiveDepthLocal] = true
          }
          result.forEach((i) => {
            if (i.group === 'archive') {
              archiveOptions.group_name = i.group_name
              archiveOptions.group_description = i.group_description
              archiveOptions.group = i.group
              archiveOptions.info.push({
                id: i.identifier,
                label: i.title,
                cost: i.cost,
              })
            }
            // } else if (i.group === 'video_quality') {
            //   videoOptions.group_name = i.group_name
            //   videoOptions.group_description = i.group_description
            //   videoOptions.info.push({
            //     id: i.identifier,
            //     label: i.title,
            //     cost: i.cost,
            //   })
            // }
            services[i.identifier] = services[i.identifier] || false
          })
          setServicesToChange(services)
          setArchiveArchiveOptions(archiveOptions)
          const archiveCost =
            archiveOptions.info.find(
              (service) => service.id === archiveDepthLocal
            )?.cost || 0
          const videoCost = 0
          setTotalPrice(archiveCost + videoCost)
        } catch (error) {
          hasFetchedCatalog.current = false
        }
      }

      fetchData()
    }
  }, [
    devices,
    setselectedDevice,
    selectedDevice,
    selected,
    dispatch,
    open,
    hasFetchedCatalog,
    setTotalPrice,
    setServicesToChange,
    isCamera,
  ])

  const formatDepth = (strDepth) => {
    const parts = strDepth.split('_')
    return parts[parts.length - 1]
  }

  const formatArchiveLabelPrice = (value, cost, id) => {
    // it is a hack
    let price = cost
    if (id === 'archive_3' && isBeward) {
      price = 0
    }
    return `${value}. ${price} ₽ ${t('common:per_day')}`
  }

  const formik = useFormik({
    initialValues: { archiveDepth },
    onSubmit: () => {
      dispatch(
        UpdateDeviceServices({
          services: servicesToChange,
          device_uuid: selected,
          device_type: isCamera ? 'camera' : 'intercom',
        })
      )
        .then(unwrapResult)
        .then(() => {
          handleClose()
          onSuccess()
        })
    },
  })

  const resetState = useCallback(() => {
    formik.setValues(archiveDepth)
  }, [formik, archiveDepth])

  const handleCloseForm = () => {
    resetState()
    handleClose()
  }

  return (
    <StyledDialog
      open={open && selected}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      onClose={handleCloseForm}
      PaperProps={{
        style: { borderRadius: 20, maxHeight: '100vh' },
      }}
      {...props}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit(e)
        }}
      >
        <DialogTitle>
          <Typography
            style={{
              fontFamily: 'Play',
              whiteSpace: 'pre-line',
            }}
            variant="h5"
          >
            {t('devices:camera_archive_settings.choose_title')}
          </Typography>
        </DialogTitle>
        <StyledContent>
          {[videoQualityOptions, archiveArchiveOptions].map((options) => (
            <StyledSection key={options.group_name}>
              <SectionTitle variant="h6">{options.group_name}</SectionTitle>
              <Typography variant="body2" color="textSecondary" paragraph>
                {options.group_description}
              </Typography>
              <OptionGroup>
                {options.info
                  ?.sort((a, b) => formatDepth(a.id) - formatDepth(b.id))
                  .map((option) => (
                    <OptionItem
                      key={option.id}
                      control={
                        <Switch
                          checked={
                            isBeward && option.id === 'archive_3'
                              ? true
                              : options.group === 'video'
                              ? videoQuality === option.id
                              : archiveDepth === option.id
                          }
                          onChange={() => {
                            if (options.group === 'video') {
                              handleVideoQualityChange(option.id, option.cost)
                            } else {
                              handleArchiveDepthChange(option.id, option.cost)
                            }
                          }}
                          disabled={
                            options.group === 'video' ||
                            (isBeward && option.id === 'archive_3')
                          }
                        />
                      }
                      label={formatArchiveLabelPrice(
                        option.label,
                        option.cost,
                        option.id
                      )}
                    />
                  ))}
              </OptionGroup>
            </StyledSection>
          ))}
          <TotalSection>
            <Typography variant="h6">
              {t('common:total')}: {totalPrice} ₽ {t('common:per_day')}
            </Typography>
          </TotalSection>
        </StyledContent>

        <StyledAction>
          <Button
            type="submit"
            primary
            variant="v2"
            fullWidth
            style={{ marginBottom: '1rem', width: '20rem' }}
            disabled={allowChange ? false : !servicesChanged}
          >
            {allowChange
              ? t('common:button.move_on')
              : t('common:button.connect')}
          </Button>
          <Button onClick={handleCloseForm} tertiary>
            {t('devices:video_archive.back')}
          </Button>
        </StyledAction>
      </form>
    </StyledDialog>
  )
}

export default ModalChangeDepthArchive
